import { daysUntil } from './dates';

export const STATUSES = [
  { value: 'todo', label: 'To do' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'needs_review', label: 'Needs review' },
  { value: 'complete', label: 'Complete' },
];
export const statusLabel = (value) => (STATUSES.find((s) => s.value === value) || {}).label || value;
const STATUS_RANK = Object.fromEntries(STATUSES.map((s, i) => [s.value, i]));

export const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
];
const PRIORITY_RANK = { high: 0, normal: 1, low: 2 };

export const SORT_OPTIONS = [
  { value: 'due', label: 'Due date' },
  { value: 'completed', label: 'Recently completed' },
  { value: 'manual', label: 'Manual order' },
  { value: 'priority', label: 'Priority' },
  { value: 'status', label: 'Status' },
  { value: 'added', label: 'Recently added' },
  { value: 'updated', label: 'Recently updated' },
  { value: 'title', label: 'Title A–Z' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'progress', label: 'Progress' },
];

export const progressOf = (task) => {
  const steps = task.checklist || [];
  if (steps.length === 0) return null;
  return { done: steps.filter((s) => s.done).length, total: steps.length };
};

export const matchesSearch = (task, query) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  return [task.title, task.description, task.assignee_name].some((v) => (v || '').toLowerCase().includes(q));
};

// The toolbar's Active / Completed toggle: a task marked complete leaves the
// active list and shows on the completed one.
export const LISTS = [
  { value: 'active', label: 'Active' },
  { value: 'complete', label: 'Completed' },
];
export const matchesList = (task, list) => (list === 'complete'
  ? task.status === 'complete'
  : task.status !== 'complete');

export const filterTasks = (tasks, list, query) => tasks.filter((t) => matchesList(t, list) && matchesSearch(t, query));

const byManual = (a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id;
const num = (x, y) => x - y;
const text = (x, y) => x.localeCompare(y, undefined, { sensitivity: 'base' });
const time = (iso) => (iso ? Date.parse(iso) : null);

// Each sort key reads one value off a task (null = "has none", always listed
// last) and says whether its natural order is descending. `manual` is the
// tie-break for every key, so a reversed sort never reverses the manual order.
const SORT_KEYS = {
  due: { value: (t, today) => daysUntil(t.due_on, today), compare: num },
  completed: { value: (t) => time(t.completed_at), compare: num, desc: true },
  priority: { value: (t) => PRIORITY_RANK[t.priority], compare: num },
  status: { value: (t) => STATUS_RANK[t.status], compare: num },
  added: { value: (t) => time(t.created_at), compare: num, desc: true },
  updated: { value: (t) => time(t.updated_at), compare: num, desc: true },
  title: { value: (t) => t.title, compare: text },
  assignee: { value: (t) => t.assignee_name || null, compare: text },
  // Furthest along first; tasks without a checklist last.
  progress: { value: (t) => { const p = progressOf(t); return p && p.done / p.total; }, compare: num, desc: true },
};

// 'asc' | 'desc': the direction a sort actually lists in, given its natural
// order and whether the viewer flipped it from the column header.
export const sortDirection = (sort, reverse = false) => {
  const desc = !!(SORT_KEYS[sort] || {}).desc;
  return desc !== !!reverse ? 'desc' : 'asc';
};

// Sort a filtered list. `today` only matters for 'due'. `reverse` flips the
// primary key only: undated/unassigned tasks stay last, ties stay in manual order.
export const sortTasks = (tasks, sort, today, reverse = false) => {
  const key = SORT_KEYS[sort];
  if (!key) return [...tasks].sort(byManual);
  const dir = sortDirection(sort, reverse) === 'desc' ? -1 : 1;
  return [...tasks].sort((a, b) => {
    const va = key.value(a, today);
    const vb = key.value(b, today);
    if (va == null && vb == null) return byManual(a, b);
    if (va == null) return 1;
    if (vb == null) return -1;
    return dir * key.compare(va, vb) || byManual(a, b);
  });
};

// Move an item within a list; returns a new list.
export const moveItem = (list, from, to) => {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};
